import React from "react";
import { resolveStatusColumn } from "@helpers/helpers";
import LegendComponent from "@components/LegendComponent";
import TriggerTypeCell from "./TriggerTypeCell";
import {
  COLUMN_KEYS,
  LEGEND_TAGS,
  TRIGGER_TYPES,
  FILTER_KEYS,
} from "./constants";
import { Calendar, Database } from "lucide-react";

/**
 * Builds the legend data configuration for status indicators
 */
export const buildLegendData = (translate) => [
  {
    text: translate("data-acquisition-instances.legend.completedWithWarning"),
    color: "#f6ad55",
    tag: LEGEND_TAGS.COMPLETED_WARNING,
  },
  {
    text: translate("data-acquisition-instances.legend.partiallyRevoked"),
    color: "#dd6b20",
    tag: LEGEND_TAGS.PARTIALLY_REVOKED,
  },
  {
    text: translate("data-acquisition-instances.legend.revoked"),
    color: "#742a2a",
    tag: LEGEND_TAGS.REVOKED,
  },
];

/**
 * Builds the column definitions for the data table
 */
export const buildColumns = (translate, legendData) => [
  {
    accessorKey: COLUMN_KEYS.STATUS,
    header: translate("data-acquisition-instances.columns.Status"),
    headerHelper: () => {
      return (
        <LegendComponent
          data={legendData}
          dataToHide={[LEGEND_TAGS.ACTIVE, LEGEND_TAGS.DISABLED]}
        />
      );
    },
    size: 150,
    sortable: true,
    canFilter: true,
    alignment: "left",
    hideCopy: true,
    isMultiple: true,
    processFilterValue: (value, row) => {
      return row.DataAcquisitionInstanceStatusTag;
    },
    processFilterKey: (value, row) => {
      return FILTER_KEYS.STATUS_TAG;
    },
    filterType: "checkbox",
    operator: "EQ",
    cell: ({ row }) => {
      return resolveStatusColumn(
        row.original.DataAcquisitionInstanceStatusName,
        row.original.DataAcquisitionInstanceStatusTag
      );
    },
  },
  {
    accessorKey: COLUMN_KEYS.DATA_ACQUISITION_NAME,
    header: translate(
      "data-acquisition-instances.columns.DataAcquisitionName"
    ),
    canFilter: true,
    isMultiple: false,
    sortable: true,
    size: 150,
    alignment: "left",
    processFilterValue: (value, row) => {
      return row.DataAcquisitionTag;
    },
    processFilterKey: (value, row) => {
      return FILTER_KEYS.ACQUISITION_TAG;
    },
    filterType: "checkbox",
    operator: "EQ",
  },
  {
    accessorKey: COLUMN_KEYS.TRIGGER_TYPE,
    header: translate("data-acquisition-instances.columns.Trigger"),
    sortable: false,
    size: 100,
    isMultiple: true,
    canFilter: false,
    alignment: "left",
    processFilterKey: (value, row) => {
      return FILTER_KEYS.TRIGGER_TYPE;
    },
    processFilterValue: (value, row) => {
      if (row.IsFileDrop) return TRIGGER_TYPES.FILE_DROP;
      if (row.IsScheduleTrigger) return TRIGGER_TYPES.SCHEDULE;
      if (row.IsManualTrigger) return TRIGGER_TYPES.MANUAL;
    },
    cell: ({ row }) => <TriggerTypeCell row={row} />,
  },
  {
    accessorKey: COLUMN_KEYS.START_DATE,
    header: translate("data-acquisition-instances.columns.StartDate"),
    size: 120,
    sortable: true,
    canFilter: true,
    filterType: "date",
    splitCellValue: "T",
  },
  {
    accessorKey: COLUMN_KEYS.END_DATE,
    header: translate("data-acquisition-instances.columns.EndDate"),
    size: 120,
    sortable: true,
    canFilter: true,
    filterType: "date",
    splitCellValue: "T",
  },
  {
    accessorKey: COLUMN_KEYS.TOTAL_RAW_RECORDS,
    header: translate("data-acquisition-instances.columns.TotalRawRecords"),
    size: 150,
    isMultiple: false,
    sortable: false,
    hideCopy: true,
    showThousandSeperator: true,
  },
  {
    accessorKey: COLUMN_KEYS.TOTAL_RECORDS_FOR_MATCHING,
    header: translate(
      "data-acquisition-instances.columns.TotalRecordsForMatching"
    ),
    size: 250,
    isMultiple: false,
    sortable: false,
    truncate: false,
    hideCopy: true,
    showThousandSeperator: true,
  },
  {
    accessorKey: COLUMN_KEYS.RECORDS_RANGE,
    header: translate("data-acquisition-instances.columns.RecordsRange"),
    size: 140,
    isMultiple: false,
    sortable: false,
    truncate: false,
    hideCopy: true,
  },
];

/**
 * Builds the filter display values configuration
 */
export const buildFilterDisplayValues = (translate) => ({
  [FILTER_KEYS.TRIGGER_TYPE]: [
    {
      displayName: translate("filters-display.Manual"),
      value: TRIGGER_TYPES.MANUAL,
      selectable: true,
    },
    {
      displayName: translate("filters-display.Schedule"),
      value: TRIGGER_TYPES.SCHEDULE,
      selectable: true,
    },
    {
      displayName: translate("filters-display.FileDrop"),
      value: TRIGGER_TYPES.FILE_DROP,
      selectable: true,
    },
  ],
  [FILTER_KEYS.STATUS_TAG]: [
    {
      displayName: translate("filters-display.All"),
      value: null,
      selectable: false,
      children: [
        {
          displayName: translate("filters-display.InProgress"),
          value: "IN_PROGRESS",
        },
        {
          displayName: translate("filters-display.Completed"),
          value: "COMPLETED",
        },
        {
          displayName: translate("filters-display.CompletedWarning"),
          value: LEGEND_TAGS.COMPLETED_WARNING,
        },
        {
          displayName: translate("filters-display.Failed"),
          value: "FAILED",
        },
      ],
    },
  ],
});

/**
 * Builds the filter configuration for the data table
 */
export const buildFilters = (
  translate,
  getDynamicFilters,
  currentDateTime
) => [
  {
    mainFilters: [
      {
        key: FILTER_KEYS.DATA_ACQUISITION_NAME,
        displayName: translate(
          "data-acquisition-instances.filters.acquisition"
        ),
        icon: () => (
          <Database size={18} />
        ),
        components: [
          {
            name: translate("data-acquisition-instances.filters.acquisition"),
            type: "title",
            key: "title",
          },
          {
            name: translate(
              "data-acquisition-instances.filters.acquisitionStreamStatus"
            ),
            title: translate(
              "data-acquisition-instances.filters.acquisitionStreamStatus"
            ),
            type: "checkboxGroup",
            searchable: false,
            key: FILTER_KEYS.STATUS_TAG,
            values: getDynamicFilters(FILTER_KEYS.STATUS_TAG),
            filterType: "checkbox",
            operator: "EQ",
          },
          {
            name: translate(
              "data-acquisition-instances.filters.acquisitionInstance"
            ),
            type: "checkboxGroup",
            searchable: false,
            key: FILTER_KEYS.ACQUISITION_TAG,
            values: getDynamicFilters(FILTER_KEYS.ACQUISITION_TAG),
            filterType: "checkbox",
            operator: "EQ",
          },
        ],
      },
      {
        key: FILTER_KEYS.ACQUISITION,
        displayName: translate("data-acquisition-instances.filters.date"),
        icon: () => (
          <Calendar size={18} />
        ),
        components: [
          {
            name: translate("data-acquisition-instances.filters.selectDate"),
            type: "title",
            key: "title",
          },
          {
            name: translate("data-acquisition-instances.filters.startDate"),
            type: "dateInput",
            inputType: "date",
            key: COLUMN_KEYS.START_DATE,
            placeHolder: translate(
              "data-acquisition-instances.filters.startDate"
            ),
            prefix: "",
            defaultValue: currentDateTime,
            filterType: "date",
            dynamicMaxDate: (selectedValues) => {
              return selectedValues?.EndDate?.value;
            },
          },
          {
            name: translate("data-acquisition-instances.filters.endDate"),
            type: "dateInput",
            inputType: "date",
            key: COLUMN_KEYS.END_DATE,
            placeHolder: translate(
              "data-acquisition-instances.filters.endDate"
            ),
            prefix: "",
            defaultValue: currentDateTime,
            filterType: "date",
            dynamicMinDate: (selectedValues) => {
              return selectedValues?.StartDate?.value;
            },
          },
        ],
      },
    ],
  },
];

/**
 * Builds the selected filters display labels
 */
export const buildSelectedFiltersDisplay = (translate) => ({
  direction: translate("filters-display.Direction"),
  [FILTER_KEYS.TRIGGER_TYPE]: translate("filters-display.TriggerType"),
  [FILTER_KEYS.DATA_ACQUISITION_NAME]: translate("filters-display.Stream"),
  [FILTER_KEYS.STATUS_TAG]: translate("filters-display.Status"),
  ScheduleDate: translate("filters-display.ScheduleDate"),
  [COLUMN_KEYS.START_DATE]: translate("filters-display.StartDate"),
  [COLUMN_KEYS.END_DATE]: translate("filters-display.EndDate"),
  [COLUMN_KEYS.RECORDS_RANGE]: translate("filters-display.RecordsRange"),
  [COLUMN_KEYS.TOTAL_RECORDS_FOR_MATCHING]: translate(
    "filters-display.TotalRecordsForMatching"
  ),
  [COLUMN_KEYS.TOTAL_RAW_RECORDS]: translate(
    "filters-display.TotalRawRecords"
  ),
  contains: translate("filters-display.Contains"),
  startWith: translate("filters-display.StartsWith"),
  [FILTER_KEYS.ACQUISITION_TAG]: translate(
    "filters-display.DataAcquisitionName"
  ),
});
